<script setup>
  import {ref} from "vue";

  import {useRemember} from "@inertiajs/vue3";
  import {patientGender} from "@pages/Patients/Enums/Enums.js";
  import PagesRecordResultPatientDataAdditionalInfo from "@pages/Records/Components/Result/PatientData/AdditionalInfo.vue";
  import PagesRecordResultPatientDataDiseases from "@pages/Records/Components/Result/PatientData/Diseases.vue";
  import PagesRecordResultPatientDataLaborvalues from "@pages/Records/Components/Result/PatientData/Laborvalues.vue";
  import PagesRecordResultPatientDataPatientAttributes from "@pages/Records/Components/Result/PatientData/PatientAttributes.vue";
  import PagesRecordResultPatientDataSymptoms from "@pages/Records/Components/Result/PatientData/Symptoms.vue";
  import {MedCheckbox, MedFormField, MedLabel} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";

  defineOptions({
    name: "PagesRecordResultPatientData",
  });

  const props = defineProps({
    /** @var Object patient is an already decrypted (!) patient */
    patient: {
      type: Object,
      required: true,
    },
    record: {
      type: Object,
      required: true,
    },
    symptoms: {
      type: Object,
      required: true,
    },
    diseases: {
      type: Object,
      required: true,
    },
    laborvalues: {
      type: Object,
      required: true,
    },
  });

  const printSettings = useRemember(
    {
      doctor: true,
      patient: true,
    },
    `PrintSettings/PatientData:${props.record.id}`,
  );
  const highlightSection = ref(false);
</script>

<template>
  <section class="border-2 border-gray-200 p-2 print:my-0!" :data-test="getTestId('patient-data')">
    <div class="-m-2 bg-gray-50 p-2">
      <div class="flex justify-between space-x-4">
        <div class="flex space-x-1">
          <span>Für:</span>

          <h3 class="inline font-bold">
            {{ patient.firstname }} {{ patient.prefix }} {{ patient.suffix }} {{ patient.lastname }}
          </h3>

          <span v-if="patient.is_fake">
            ({{ record.age ? record.age + " Jahre, " : "" }}{{ patient.gender ? patientGender[patient.gender] : "" }})
          </span>

          <span v-else>
            ({{ patient.birthdate ? "Geb. " + patient.birthdate_formatted + ", " : ""
            }}{{ record.age ? record.age + " Jahre, " : "" }}{{ patient.gender ? patientGender[patient.gender] : "" }})
          </span>
        </div>

        <div
          class="text-right text-sm print:hidden"
          role="button"
          tabindex="0"
          @mouseover="highlightSection = true"
          @mouseout="highlightSection = false"
          @focusin="highlightSection = true"
          @focusout="highlightSection = false"
        >
          <div>Patientenangaben übernehmen in PDF</div>

          <div class="flex justify-end space-x-4">
            <MedFormField orientation="horizontal">
              <MedCheckbox v-model="printSettings.doctor" :data-test="getTestId('overview-print-doc-checkbox')" />
              <MedLabel>an Arzt</MedLabel>
            </MedFormField>

            <MedFormField orientation="horizontal">
              <MedCheckbox v-model="printSettings.patient" :data-test="getTestId('overview-print-patient-checkbox')" />
              <MedLabel>an Patienten</MedLabel>
            </MedFormField>
          </div>
        </div>
      </div>

      <p
        :class="
          cn('text-xs text-gray-500', {
            'in-[.print-doc]:print:hidden': !printSettings.doctor,
            'in-[.print-patient]:print:hidden': !printSettings.patient,
          })
        "
      >
        Folgende Eingabedaten wurden der Apotheke (vom Patienten) mitgeteilt:
      </p>
    </div>

    <dl
      :class="
        cn('mt-4 break-before-avoid-page divide-y divide-gray-200', {
          'in-[.print-doc]:print:hidden': !printSettings.doctor,
          'in-[.print-patient]:print:hidden': !printSettings.patient,
          'bg-yellow-50': highlightSection,
        })
      "
    >
      <PagesRecordResultPatientDataAdditionalInfo
        :record="record"
        class="print:break-inside-avoid-page"
        :test-id="getTestId('patient-data')"
      />

      <PagesRecordResultPatientDataSymptoms
        :symptoms="symptoms"
        class="print:break-inside-avoid-page"
        :test-id="getTestId('patient-data')"
      />

      <PagesRecordResultPatientDataDiseases
        :diseases="diseases"
        class="print:break-inside-avoid-page"
        :test-id="getTestId('patient-data')"
      />

      <PagesRecordResultPatientDataLaborvalues
        :laborvalues="laborvalues"
        :record="record"
        class="print:break-inside-avoid-page"
        :test-id="getTestId('patient-data')"
      />

      <PagesRecordResultPatientDataPatientAttributes
        :patient="patient"
        class="print:break-inside-avoid-page"
        :test-id="getTestId('patient-data')"
      />
    </dl>
  </section>
</template>
